import { applyRate, lineAmount, MoneyError } from './money.js';
import { chargedRate, defaultVatCode, vatCode } from './vat-codes.js';
/**
 * Invoice arithmetic shared by the API, the web and the mobile app.
 *
 * - Item lines: quantity (thousandths) × unit price (cents), rounded per line.
 * - VAT is computed per rate group on the group's net total (one rounding per rate), so the
 *   VAT breakdown printed under the table always adds up.
 * - A percent discount applies to every rate group in proportion; a fixed discount belongs to one
 *   rate (required when the invoice has more than one rate).
 * - A seller that is not a VAT payer has no VAT: every rate is treated as 0 and no breakdown is shown.
 * - A credit invoice (kreditinė) is entered with positive lines; its totals come out negative.
 * - Each line may carry its VAT classifier code (PVM1, PVM12, PVM25, …). The code decides the rate the invoice
 *   charges (0 where the buyer accounts for the VAT or there is none) and the VAT group; a line without a code
 *   stands for the domestic code of its rate (21 % PVM1, 12 % PVM58, 9 % PVM2, 5 % PVM3).
 *
 * To be confirmed with the accountant (see GELISTIRME_PLANI.md): rounding per rate group,
 * half-away-from-zero rounding, and how discounts are split across rates.
 */
export const INVOICE_KINDS = ['invoice', 'vat_invoice', 'proforma', 'credit'];
/** VAT rates used in Lithuania, in basis points. */
export const LT_VAT_RATES = [2100, 1200, 900, 500, 0];
export class InvoiceError extends Error {
}
export function calculateInvoice(lines, options) {
    const vatPayer = options.sellerIsVatPayer;
    const calculated = [];
    // VAT groups by (rate charged, code).
    const groups = new Map();
    const keyOf = (rate, code) => `${rate}|${code ?? ''}`;
    const codeOf = (index, code, rate) => {
        if (!vatPayer)
            return undefined;
        if (code !== undefined && !vatCode(code))
            throw new InvoiceError(`line ${index + 1}: unknown VAT code ${code}`);
        return code ?? defaultVatCode(rate);
    };
    lines.forEach((line, index) => {
        if (line.kind !== 'item')
            return;
        if (line.quantity === 0)
            throw new InvoiceError(`line ${index + 1}: quantity is zero`);
        if (vatPayer && line.vatRate < 0)
            throw new InvoiceError(`line ${index + 1}: negative VAT rate`);
        const net = lineAmount(line.quantity, line.unitPrice);
        calculated.push({ index, kind: 'item', net });
        const rate = !vatPayer ? 0 : line.vatCode ? chargedRate(line.vatCode, line.vatRate) : line.vatRate;
        const code = codeOf(index, line.vatCode, rate);
        const key = keyOf(rate, code);
        const g = groups.get(key) ?? { rate, code, net: 0 };
        g.net += net;
        groups.set(key, g);
    });
    const subtotal = [...groups.values()].reduce((a, g) => a + g.net, 0);
    const itemGroups = new Map([...groups].map(([k, g]) => [k, { ...g }]));
    let discountTotal = 0;
    lines.forEach((line, index) => {
        if (line.kind !== 'discount')
            return;
        if (line.value <= 0)
            throw new InvoiceError(`line ${index + 1}: discount must be positive`);
        let amount = 0;
        if (line.mode === 'percent') {
            if (line.value > 10000)
                throw new InvoiceError(`line ${index + 1}: discount over 100 %`);
            for (const [key, item] of itemGroups) {
                const share = applyRate(item.net, line.value);
                groups.get(key).net -= share;
                amount += share;
            }
        }
        else {
            let key;
            if (!vatPayer)
                key = keyOf(0, undefined);
            else {
                const candidates = [...itemGroups.entries()].filter(([, g]) => line.vatCode ? g.code === line.vatCode : line.vatRate === undefined || g.rate === line.vatRate);
                if (line.vatRate === undefined && !line.vatCode && itemGroups.size !== 1)
                    throw new InvoiceError(`line ${index + 1}: choose the VAT rate this discount reduces`);
                if (candidates.length > 1)
                    throw new InvoiceError(`line ${index + 1}: choose the VAT code this discount reduces`);
                key = candidates[0]?.[0];
            }
            if (!key || !itemGroups.has(key))
                throw new InvoiceError(`line ${index + 1}: no items with this VAT rate`);
            groups.get(key).net -= line.value;
            amount = line.value;
        }
        calculated.push({ index, kind: 'discount', net: -amount });
        discountTotal += amount;
    });
    for (const g of groups.values()) {
        if (g.net < 0 && subtotal > 0)
            throw new InvoiceError(`discounts exceed the items with VAT rate ${g.rate / 100} %`);
    }
    const vatGroups = vatPayer
        ? [...groups.values()].sort((a, b) => b.rate - a.rate || (a.code ?? '').localeCompare(b.code ?? ''))
            .map((g) => ({ rate: g.rate, ...(g.code ? { code: g.code } : {}), net: g.net, vat: applyRate(g.net, g.rate) }))
        : [];
    const vatTotal = vatGroups.reduce((sum, g) => sum + g.vat, 0);
    const net = subtotal - discountTotal;
    calculated.sort((a, b) => a.index - b.index);
    const totals = { lines: calculated, subtotal, discountTotal, net, vatGroups, vatTotal, total: net + vatTotal };
    return options.kind === 'credit' ? negate(totals) : totals;
}
function negate(t) {
    const flip = (v) => (v === 0 ? 0 : -v);
    return {
        lines: t.lines.map((l) => ({ ...l, net: flip(l.net) })),
        subtotal: flip(t.subtotal),
        discountTotal: flip(t.discountTotal),
        net: flip(t.net),
        vatGroups: t.vatGroups.map((g) => ({ ...g, net: flip(g.net), vat: flip(g.vat) })),
        vatTotal: flip(t.vatTotal),
        total: flip(t.total),
    };
}
/** Invoice number as printed: numbers keep at least `width` digits ("07"). */
export function formatInvoiceNumber(value, width = 2) {
    if (!Number.isSafeInteger(value) || value < 1)
        throw new MoneyError('invoice number must be a positive whole number');
    return value.toString().padStart(width, '0');
}
//# sourceMappingURL=invoice.js.map